/**
 * Converts a SAP catalog {@link Item} into product-block data.
 *
 * Shared by the manual block editors (catalog picker) and the AI assistant's
 * `add-product` / `add-product-list` operations, so a product looks identical
 * however it lands in the draft.
 *
 * `price` is left blank on purpose: it is an override only. A blank price is
 * resolved per recipient on render from that recipient's business-partner
 * price list, so each recipient sees their own individual price. The user
 * fills the field only to force a fixed price for everyone.
 */

import type { Item } from '@/api/items/items.types.ts';
import type {
  ProductCardBlockData,
  ProductListItem,
  ProductSpotlightBlockData,
} from '@/api/email-builder/email-builder.types.ts';
import { itemLinkFor } from './email-builder.constants';

/** Builds product-card data from a catalog item. */
export function cardFromItem(item: Item): ProductCardBlockData {
  return {
    name: item.name,
    articleNumber: item.id,
    imageUrl: item.imageUrl ?? '',
    price: '',
    manufacturer: item.manufacturer,
    href: itemLinkFor(item.id),
  };
}

/** Builds product-spotlight data from a catalog item. */
export function spotlightFromItem(item: Item): ProductSpotlightBlockData {
  return {
    name: item.name,
    articleNumber: item.id,
    description: '',
    imageUrl: item.imageUrl ?? '',
    price: '',
    manufacturer: item.manufacturer,
    buttonLabel: 'Jetzt bestellen',
    buttonHref: itemLinkFor(item.id),
  };
}

/** Builds one product-list row from a catalog item. */
export function productListItemFromItem(item: Item): ProductListItem {
  return {
    name: item.name,
    articleNumber: item.id,
    price: '',
  };
}
